/**
 * 사무직이 읽는 글의 규칙. 답·작성 담당·보고서 제목이 함께 쓴다.
 *
 * 규칙 없이 두면 모델은 가장 흔한 말로 수렴한다 — 「현황을 정리했습니다」「확인해 보시기
 * 바랍니다」, 끝에 앞말을 되풀이하는 요약. 보고서가 그 말로 채워졌다(실제 테넌트, 2026-10-23).
 * 다른 모듈을 불러오지 않는다 — compute·experts 가 모두 이것을 불러온다.
 */
export const PLAIN_WRITING = `글 쓰는 법:
- 첫 문장에 결론을 숫자로 쓴다. 「이번 달 완료 ○○건, 지난달보다 ○○건 적다」처럼.
- 「정리했습니다」「준비했습니다」「확인해 보시기 바랍니다」「살펴보겠습니다」 같은 일 보고 말을 쓰지 않는다.
- 「매우」「다양한」「효율적인」「체계적인」「종합적으로」 같은 꾸밈말을 쓰지 않는다.
- 앞에서 한 말을 끝에서 다시 요약하지 않는다. 「~이 아니라 ~이다」 꼴로 꾸미지 않는다.
- 판단할 수 없는 것은 한 번만, 왜 못 하는지와 함께 쓴다.
- 셀 수 있는 것은 「많다」 대신 숫자로 쓴다.`;


/**
 * 보고 문서(PDF·워드·발표자료)의 글 — 공문서 개조식. 채팅 답은 「~입니다」로 둔다.
 * 국가직 팀장 검토(2026-10-23): 「~다.」 평서문, 화면 안내, 자기 보증, 조회 인자가 보고서에 실려 돌려보낼 문서였다.
 */
export const REPORT_STYLE = `보고 문서 쓰는 법(공문서 개조식):
- 문장은 명사형으로 끝낸다(「~함」「~임」「~필요」 또는 명사). 「~다」「~습니다」「~입니다」로 끝내지 않는다.
- 요약은 세 항목 — 실적(건수·비율), 주의할 것(경과 건수와 쏠림), 판단할 수 없는 것(예: 적합·부적합 판정 항목이 조회 자료에
  없어 이상 여부 판단 불가). 판단할 수 없는 것은 빠진 자료를 구체적으로 쓴다 — 조회한 화면 이름과 같은 말(「점검 결과 미조회」)은
  그 화면을 안 본 것처럼, 「미제공」은 누가 주지 않은 것처럼 읽힌다. 한 항목에 한 사실만 쓴다(「~이며, ~임」으로 묶지 않는다).
- 필수인지 모르는 칸이 비어 있으면 「누락」이 아니라 「○○ 없음 N건, 필수 여부 확인 필요」로 쓴다(건수는 근거·갈래 결과의 값).
  아직 하지 않은 건(미실시)에 비어 있는 칸(결과일자·처리자·서명 등)은 비어 있는 것이 맞다 — 확인 필요 사항으로 쓰지 않는다.
- 문서의 기간(제목의 달) 밖 숫자는 쓰지 않는다 — 갈래 결과가 여러 달로 셌으면 그 수는 싣지 않는다.
- 한 줄에 같은 말을 겹쳐 쓰지 않는다(「대상 아님 106건의 대상 제외 사유」가 아니라 「대상 아님 106건 제외 사유 확인 필요」).
- 기간이 다른 숫자는 한 문장에 섞지 않는다. 여러 달을 합친 수는 달별 몫을 밝힌다(「111건 = 9월분 51건 + 10월분 60건」).
- 요약 첫 줄은 사용자 질문에 대한 판단이다. 판단할 자료가 없으면 「○○ 미조회로 판단 불가」라고 쓰고, 다른 사실로 채우지 않는다.
- 숫자에는 대상·단위·기간을 붙인다(「10월 점검 대상 ○○건」). 전체 대비 부분은 근거의 비율을 함께 쓴다.
- 점검 대상·전체·완료·미실시 건수는 〈표 1〉의 수로 쓴다. 갈래 결과가 날짜 칸으로 자르는 등 다른 기준으로 센 수는
  같은 이름으로 쓰지 않는다.
- 끝낸 뒤에 채우는 칸(서명 등)의 없음 건수는 끝낸 건 중에서 센 수로 쓴다(「10월 완료 1,952건 중 고객 서명 없음 1,810건」).
  아직 안 한 건이 섞인 수는 쓰지 않는다.
- 근거에 완료 점검 메모(비고)의 이상 기재가 있으면 「판단 불가」로만 쓰지 않는다. 메모는 판정 항목이 아니라 메모 문구이고,
  메모가 붙은 행 수를 이상 건수로 쓰지 않는다.
- 순위·최댓값·「A가 B보다 N 많다」는 그 차이가 판단에 쓰일 때만 쓴다. 규모의 차이를 실적의 차이로 쓰지 않는다.
- 진행 중인 기간의 미완료는 기한이 지난 것(경과)과 아직 남은 것(예정)을 나눈다. 나눌 자료가 없으면 「미집계」라고 쓴다.
- 비교 기준(전월·전년·목표)이 근거에 있으면 쓰고, 없으면 지어내지 않는다.
- 기간(예: 2026년 10월)은 제목에 한 번만 쓰고 항목마다 되풀이하지 않는다.
- 같은 숫자를 요약·그림 문장에서 되풀이하지 않는다. 그림 위 줄에는 그 그림만 보여 주는 것(쏠림·경과)을 쓴다.
- 향후 조치는 업무 조치다 — 누가(담당)·무엇을(대상·건수)·언제까지(안). 기한은 근거의 날짜(이번 달 말일 등)로만 제안한다.
- 향후 조치는 실행 조치(실시·완료·확정)로 쓴다. 「확인」만 하는 조치, 확인 필요 사항과 같은 말은 쓰지 않는다
  (근거가 짚은 최근 예정분의 실시·입력 여부와, 배정분을 하나도 못 한 담당의 미실시 사유는 확인 조치로 쓴다).
- (근거에 마감된 달분이 없을 때) 근거가 경과를 「N~M일 경과」와 「최근 예정(직전 근무일 또는 날짜)」으로 나눴으면 요약 첫 줄은 오래 지난 묶음으로 쓴다 —
  예: 예정일보다 11~24일 지난 미실시 52건 우선 조치 필요. 같은 경과를 요약 두 줄로 쓰지 않는다.
  조치는 둘로: 오래 지난 건은 점검 완료(이번 달 말일까지), 최근 예정분은 다음 근무일까지 확인
  예: '26.10.23. 예정 7건 실시·입력 여부 확인('26.10.26.까지, ○○).
- 「최근」「직전」 같은 시점어 대신 날짜를 쓴다(「최근 예정분 8건」이 아니라 「'26.10.23.~'26.10.24. 예정 8건」).
- 근거에 마감된 달(전월)분이 있으면(「9·10월 미실시 73호기(111건) = …」) 아래가 위의 경과 규칙보다 먼저다:
  제목 기간은 기준월로 둔다 — 사용자가 두 달을 말했을 때만 두 달. 전월분은 「전월(9월)분 미실시」로 따로 쓴다.
  실적은 기준월로 쓴다(「10월(25일 현재) 점검 대상 2,348건 중 완료 1,952건(83.1%)」) — 여러 달을 합친 건수·완료율은 쓰지 않는다.
  항목마다 어느 달 숫자인지 앞에 쓴다(제목에 한 번만 쓰는 규칙보다 먼저).
  같은 호기를 두 달에 센 건수와 호기를 가른다(「103건」이 아니라 「65호기」). 요약 첫 줄은 「두 달 모두 미실시」 묶음으로,
  그 묶음의 현장·담당을 괄호에 쓴다. 예: 9·10월 모두 점검하지 않은 38호기 우선 조치 필요(35호기는 ○○현장, ○○ 담당).
  조치는 묶음별로 쓰고 두 달 모두 미실시를 먼저 — 사유 확인은 그 묶음의 현장·호기 수로 정확히 쓴다.
  마감된 달과 진행 중인 달의 비율을 견주지 않는다(「9월 2.2%, 10월 16.9%」를 나란히 놓지 않는다).
- 근거에 「배정 N건 중 완료 0건」인 담당이 있으면 조치 맨 앞에 「○○ 담당 N건 미실시 사유 확인(기한)」을 쓴다.
  원인은 짐작해 쓰지 않는다. 확인 조치의 기한은 다음 근무일이다 — 실행 조치(완료)보다 앞선다.
- 향후 조치는 「대상 건수 + 행위 + (기한, 담당)」 명사형으로 끝낸다. 예: 경과 52건 점검 완료('26.10.31.까지, ○○ 49·△△ 3).
  「~함」으로 끝내면 이미 끝난 일처럼 읽힌다.
- 요약 첫 줄은 숫자가 든 결론이다 — 예: 예정일 경과 미실시 52건 우선 조치 필요. 「진행 중임」처럼 아무것도 말하지 않는 줄은 쓰지 않는다.
- 비율은 무엇의 몇 건 중 몇 건인지와 함께 — 「경과 52건 중 49건(94.2%)」. 「쏠림임」처럼 명사에 「임」을 붙이지 않는다.
  자료·계산을 다시 확인하는 일은 우리 쪽 숙제라 쓰지 않는다.
- 판정 기준(코드 2=완료 등)은 확인된 것이다. 「계산 방식 미확인」처럼 제 숫자를 의심하는 말을 쓰지 않는다.
- 화면 조작 안내(받기·단추·클릭·아래·엑셀로), 「확정된」「검증된」 같은 자기 보증, 칸 이름과 조회 조건(202608, 「현장명별」),
  상태 코드를 옮긴 말(「계획 상태」 대신 「미실시」)을 쓰지 않는다.`;

/**
 * 점검이 아닌 업무(연차·매출 같은 목록)의 보고 문서 글. 점검 규칙(경과·호기·묶음, 요약 셋째 줄 「판단 불가」)이 연차 문서에
 * 실려 「생성 기준값이 없어 연차 생성 적정성 판단 불가」로 끝났다(실측 2026-10-26).
 */
export const GENERAL_REPORT_STYLE = `보고 문서 쓰는 법(공문서 개조식):
- 문장은 명사형으로 끝낸다(「~함」「~임」「~필요」 또는 명사). 「~다」「~습니다」「~입니다」로 끝내지 않는다.
- 요약은 2~3항목: 질문에 대한 판단(숫자가 든 결론), 그 판단의 근거 숫자(비율 포함), 쏠림이나 먼저 챙길 대상. 한 항목에 한 사실만 쓴다.
- 판단할 수 없는 것은 질문의 판단에 꼭 필요한 자료가 없을 때만 확인 필요 사항에 쓴다. 조회 조건의 설명이 없다는 것,
  판단에 쓰지 않는 칸이 없다는 것은 쓰지 않는다.
- 숫자에는 대상과 단위를 붙인다. 전체 대비 부분은 근거의 비율을 함께 쓴다.
- 비교 기준(전년·목표)이 근거에 있으면 쓰고, 없으면 지어내지 않는다. 순위는 판단에 쓰일 때만 쓴다.
- 향후 조치는 「대상 + 행위 + (기한, 담당)」 명사형으로 끝낸다. 기한은 근거의 기한 후보에서만 고르고, 담당은 근거에 나온
  부서나 직책만 쓴다. 「~함」으로 끝내면 이미 끝난 일처럼 읽힌다.
- 향후 조치는 보고받는 관리자가 지시할 일을 일의 차례대로 쓴다. 앞 단계는 이른 기한, 마지막 단계는 늦은 기한으로 조치마다
  기한 후보에서 고르고, 모든 조치에 같은 기한을 붙이지 않는다.
- 독려, 안내처럼 끝이 없는 일에는 기한을 붙이지 않는다. 취합, 점검, 결과 보고처럼 끝이 있는 일로 쓴다. 근거에 앞 턴의 직원 공지가
  있으면 그 공지 게시를 첫 조치로 쓴다. 직원이 할 일(신청, 협의)은 관리자의 조치로 바꿔 쓴다(「사용 계획 취합과 점검」).
- 근거에 앞 턴 공지의 직원 기한이 있으면 취합·점검 기한은 그 뒤 날짜로 고른다(직원이 낸 뒤에 모은다).
- 법 조항, 회사 규정, 보상 여부처럼 근거에 없는 제도 이야기는 지어내지 않는다.
- 기준일과 작성 부서는 문서 머리에 있다. 항목에 「○○ 기준」 날짜를 쓰지 않는다. 제목의 기간은 질문이나 자료의 기간만 쓴다.
- 같은 숫자를 요약과 그림 문장에서 되풀이하지 않는다. 그림 위 줄에는 그 그림만 보여 주는 것을 쓴다.
- 칸 이름을 그대로 옮기지 말고 사람 말로 쓴다(「연차잔여일수」가 아니라 「잔여 연차」).
- 화면 조작 안내(받기·단추·클릭·아래·엑셀로), 자기 보증(「확정된」「검증된」), 조회 조건 값, 계산 방식 이야기를 쓰지 않는다.`;

/**
 * 발견된 문제가 남아 있는 자료(결함, 고장, 민원)의 보고 문서 글. 점검 규칙의 경과·「점검 완료」 조치가 중대결함 문서에 실려
 * 「예정일 경과 102건 점검 완료」가 나갔다(실제 테넌트, 2026-10-27). 이미 찾은 문제라 할 일은 처리다.
 */
export const ISSUE_REPORT_STYLE = `보고 문서 쓰는 법(공문서 개조식, 발견된 문제가 남아 있는 자료):
- 문장은 명사형으로 끝낸다(「~함」「~임」「~필요」 또는 명사). 「~다」「~습니다」「~입니다」로 끝내지 않는다.
- 이 자료의 행은 이미 발견된 문제(결함, 고장, 민원)다. 할 일을 안 한 것이 아니다. 「미실시」, 「예정일 경과」, 「점검 완료」,
  「실시·입력 여부」를 쓰지 않는다.
- 요약은 2~3항목: 급한 문제의 건수와 처리 상태(숫자가 든 결론), 쏠림(한 현장이나 한 항목에 몰린 것과 그 몫), 반복되는 문제.
  한 항목에 한 사실만 쓴다.
- 확인 필요 사항은 근거에서 나온 것만 0~2항목. 예: 한 현장에 몰린 건이 한 번의 점검에서 나온 것인지, 처리 결과를 적는 칸이 비어 있는 것.
- 향후 조치는 처리 일이다. 급한 것부터 「대상 건수 + 처리 행위 + (기한, 담당)」 명사형으로 쓴다. 예: ○○ 39건 수리 완료(기한, 담당),
  △△ 65건 처리 일정 확정(기한). 기한은 근거의 기한 후보에서만 고르고, 담당은 근거에 나온 현장 담당이나 부서만 쓴다.
- 원인은 짐작해 쓰지 않는다. 쏠림은 「살펴볼 필요」까지만 쓴다.
- 숫자에는 대상과 단위를 붙인다. 전체 대비 부분은 근거의 비율을 함께 쓴다.
- 같은 숫자를 요약과 그림 문장에서 되풀이하지 않는다. 그림 위 줄에는 그 그림만 보여 주는 것을 쓴다.
- 칸 이름을 그대로 옮기지 말고 사람 말로 쓴다.
- 화면 조작 안내(받기·단추·클릭·아래·엑셀로), 자기 보증(「확정된」「검증된」), 조회 조건 값, 계산 방식 이야기를 쓰지 않는다.`;

/** 열린 문제 문서에 실으면 안 되는 말(점검 미실시의 말). 보고 문장 검사에 더한다. */
const NOT_ISSUE = [/미실시/, /예정일\s*(?:경과|이내|보다)/, /(?<!재)점검\s*완료/, /실시\s*[·ㆍ,]?\s*입력/];

/** 열린 문제 문서의 개조식 문장인가. */
export function issueLint(text: string): boolean {
  return reportLint(text) && !NOT_ISSUE.some((pattern) => pattern.test(text));
}

/**
 * 공지(사내 게시용 안내문)의 글. 읽는 사람은 직원 전체다. 보고(판단, 확인 필요, 조치안)가 아니라 안내다. 공지판 문서가
 * 보고서 골격으로 「…판단 불가」에서 끝났고(실측 2026-10-26), 고친 판도 개조식 「~안내」, 「~바람」이라 게시물이 아니었다
 * (국가직 팀장 검토). 게시물은 항목 이름이 붙은 번호 항목과 경어다. 번호와 문의처, 게시일, 발신 부서는 문서가 붙인다.
 */
export const NOTICE_STYLE = `공지 문서 쓰는 법(사내 게시용 안내문):
- 읽는 사람은 직원 전체다. 판단·평가, 「판단 불가」, 「확인 필요」 같은 내부 검토 말은 쓰지 않는다.
- 항목은 「항목 이름: 내용」 꼴로 쓴다. 항목 이름은 짧은 명사(목적, 대상, 사용 기한, 유의 사항)다. 번호는 문서가 붙인다.
- 직원에게 하는 경어로 쓴다(「~입니다」, 「~하여 주시기 바랍니다」). 「~해요」, 「~하세요」, 개조식 「~바람」은 쓰지 않는다.
- 대상 항목에는 어떤 직원인지 직원이 알아듣는 말(「○○이 남아 있는 직원」처럼)로 쓴다. 인원수와 합계는 쓰지 않는다(확인 중인
  계정이 섞여 있어 숫자가 바뀔 수 있다). 조회 조건의 말(「0보다 큰」, 「○○ 기준으로 생성한」)은 옮기지 않는다.
- 할 일마다 기한을 쓴다. 기한은 근거의 기한 후보에서만 고르고, 먼저 할 일(계획 제출)은 이른 기한, 마지막 일은 늦은 기한이다.
- 무엇을 내는 일에는 어디에 내는지 쓴다(근거나 질문에 없으면 소속 부서장).
- 신청 방법은 질문이 청했거나 근거에 나왔을 때만 쓴다. 화면 이름이나 결재 절차를 지어내지 않는다.
- 문의처, 발신 부서, 게시일은 문서가 붙인다. 쓰지 않는다.
- 개인 이름과 개인별 수치는 쓰지 않는다. 부서별 수치는 근거에 있는 것만 쓴다.
- 법 조항, 회사 규정, 보상 여부처럼 근거에 없는 제도 이야기는 지어내지 않는다. 기한은 회사가 권하는 날짜다. 「소멸」, 「법정」
  같은 말을 붙이지 않는다.
- 항목에 「○○ 기준」 날짜를 쓰지 않는다. 제목의 기간은 질문이나 자료의 기간만 쓴다.
- 화면 조작 안내(받기·단추·클릭·아래·엑셀로), 자기 보증(「확정된」「검증된」), 조회 조건 값, 계산 방식 이야기를 쓰지 않는다.`;

/** 보고 문서에 실으면 안 되는 말. 걸린 문장은 버린다 — 모델이 규칙을 지키기를 바라기만 하지 않는다. */
const NOT_REPORT = [
  // 「필요」의 「요」는 어미가 아니다 — 「~어요·~해요·~네요·~세요」만 존댓말 어미로 본다.
  /(?:[다죠]|[어아해네세]요)\.?\s*$/, /습니다|입니다|합니다|됩니다/,
  /받기|단추|버튼|클릭|실었|받으실|아래\s*(?:표|파일)/,
  /확정된|검증된/,
  /(?:19|20)\d{2}(?:0[1-9]|1[0-2])(?:[0-3]\d)?(?!\d)/,
  /계획\s*상태/,
  // 우리 쪽 숙제 — 요약이 「계산 방식 미확인으로 판단 제한」이라 제 숫자를 의심했다(국가직 팀장 2차 검토, 2026-10-23).
  /계산\s*방식|집계\s*방식|산정\s*방식|화면\s*자료|결과\s*코드|원자료와?\s*대조|정해\s*둔/,
  // 코드값을 옮긴 줄 — 「전송결과 상태값 1인 9건의 처리 상태 구분 필요」(실제 테넌트, 2026-10-25). 뜻을 모르는 값은 우리 쪽 숙제다.
  /(?:상태|코드|구분)\s*값\s*['"「]?[0-9A-Za-z]{1,4}['"」]?\s*인/,
];

/** 개조식 보고 문장인가. */
export function reportLint(text: string): boolean {
  const line = text.trim();
  return !!line && !NOT_REPORT.some((pattern) => pattern.test(line));
}

/**
 * 공지 문장인가. 공지는 경어(「~입니다」, 「~하여 주시기 바랍니다」)로 쓴다. 해요체(「~이에요」, 「~하세요」)와 내부 검토 말
 * (「판단 불가」)은 버린다. 화면 안내·자기 보증·8자리 날짜·상태 코드는 보고 문장과 같이 버린다.
 */
const NOT_NOTICE = [
  /(?:[어아해네세에예]요)\.?\s*$/, /판단\s*불가/,
  // 반말 평서문(「~사용한다.」)과 개조식 「~바람」도 게시물 말이 아니다(독립 재검토). 「~입니다」의 「니다」는 받는다.
  /(?<!니)다\s*(?:\([^)]*\))?\.?\s*$/, /바람\s*(?:\([^)]*\))?\.?\s*$/,
  ...NOT_REPORT.slice(2),
];

export function noticeLint(text: string): boolean {
  const line = text.trim();
  return !!line && !NOT_NOTICE.some((pattern) => pattern.test(line));
}
